import { useState } from "react";
import { ArrowLeft, Upload, CheckCircle, Mail, Phone } from "lucide-react";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

const DEBT_TYPES = ["Working Capital", "Short Term Loan", "Long Term Loan", "Term Loan", "Overdraft", "Letter of Credit", "Buyers Credit"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF"];
const RATING_PROVIDERS = ["CRISIL", "ICRA", "CARE", "India Ratings", "Moody's", "S&P", "Fitch", "None"];
const RATINGS = ["AAA", "AA+", "AA", "AA-", "A+", "A", "A-", "BBB+", "BBB", "BBB-", "BB+", "BB", "BB-", "B", "C", "D", "NR", "Not Rated"];



export default function RaiseDebt({ onBack }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [termSheetFile, setTermSheetFile] = useState(null);
  const [ratingFile, setRatingFile] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    debt_type: "",
    currency: "INR",
    amount_required: "",
    purpose: "",
    tenure_months: "",
    pricing_expectation: "",
    collateral: "",
    existing_banker: "",
    credit_rating: "",
    rating_provider: "",
    annual_turnover: "",
    ebitda: "",
    existing_debt: "",
    additional_notes: "",
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

const validateStep1 = () => {
    if (!form.company_name) { alert("Company name is required."); return false; }
    if (!form.contact_name) { alert("Contact name is required."); return false; }
    if (!form.email) { alert("Email address is required."); return false; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) { alert("Please enter a valid email address."); return false; }
    if (!form.phone) { alert("Phone number is required."); return false; }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) { alert("Phone number must be at least 10 digits."); return false; }
    if (phoneDigits.length > 13) { alert("Phone number cannot exceed 13 digits."); return false; }
    return true;
  };
  const validateStep2 = () => {
    if (!form.debt_type) { alert("Debt type is required."); return false; }
    if (!form.amount_required) { alert("Amount required is required."); return false; }
    if (!form.currency) { alert("Currency is required."); return false; }
    if (!form.purpose) { alert("Purpose of loan is required."); return false; }
    if (!form.tenure_months) { alert("Tenure is required."); return false; }
    if (!form.pricing_expectation) { alert("Pricing expectation is required."); return false; }
    return true;
  };

  const validateStep3 = () => {
    if (!form.annual_turnover) { alert("Annual turnover is required."); return false; }
    if (!form.ebitda) { alert("EBITDA is required."); return false; }
    if (!form.existing_debt) { alert("Existing debt is required."); return false; }
    if (!form.credit_rating) { alert("Credit rating is required."); return false; }
    if (!form.rating_provider) { alert("Rating provider is required."); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!disclaimerAccepted) {
      alert("Please accept the disclaimer before submitting.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount_required: form.amount_required ? parseFloat(form.amount_required) : null,
        tenure_months: form.tenure_months ? parseFloat(form.tenure_months) : null,
        pricing_expectation: form.pricing_expectation ? parseFloat(form.pricing_expectation) : null,
        annual_turnover: form.annual_turnover ? parseFloat(form.annual_turnover) : null,
        ebitda: form.ebitda ? parseFloat(form.ebitda) : null,
        existing_debt: form.existing_debt ? parseFloat(form.existing_debt) : null,
        term_sheet_filename: termSheetFile ? termSheetFile.name : null,
        rating_rationale_filename: ratingFile ? ratingFile.name : null,
        disclaimer_accepted: true,
        status: "Pending",
      };

      await fetch(`${SUPABASE_URL}/rest/v1/debt_raise_requests`, {
        method: "POST",
        headers: {
          "apikey": SUPABASE_ANON,
          "Authorization": `Bearer ${SUPABASE_ANON}`,
          "Content-Type": "application/json",
          "Prefer": "return=minimal"
        },
        body: JSON.stringify(payload)
      });

      setSubmitted(true);
    } catch (e) {
      alert("Something went wrong. Please try again.");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // ── SUCCESS SCREEN ──
  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-emerald-400" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-3">Request Submitted!</h2>
        <p className="text-gray-400 mb-2 text-lg">We will get back to you shortly.</p>
        <p className="text-gray-500 text-sm mb-8">
          Our team at Nebula Finance will review your request and reach out to you at <span className="text-indigo-400">{form.email}</span> shortly from our banking network.
        </p>
        <div className="nebula-card p-6 mb-8 text-left">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Request Summary</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-gray-500">Company</p><p className="text-white font-medium">{form.company_name}</p></div>
            <div><p className="text-gray-500">Debt Type</p><p className="text-white font-medium">{form.debt_type}</p></div>
            <div><p className="text-gray-500">Amount Required</p><p className="text-white font-medium">{form.currency} {parseFloat(form.amount_required).toLocaleString()}</p></div>
            <div><p className="text-gray-500">Pricing Expectation</p><p className="text-white font-medium">{form.pricing_expectation}% p.a.</p></div>
            <div><p className="text-gray-500">Tenure</p><p className="text-white font-medium">{form.tenure_months} months</p></div>
            <div><p className="text-gray-500">Status</p><p className="text-emerald-400 font-medium">Pending Review</p></div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-6 text-sm text-gray-400 mb-8">
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-400" /><span>founder@nebulafinance.net</span></div>
        </div>
        <button onClick={onBack} className="nebula-btn-primary px-8 py-3">Back to Debt Management</button>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Raise Debt</h2>
          <p className="text-gray-400 text-sm">Connect with our banking network for competitive financing</p>
        </div>
      </div>

      {/* Hero banner */}
      <div className="nebula-card p-6 mb-6 bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border-indigo-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-xl">N</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">Nebula Finance — Your Debt Raising Partner</h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-3">
              Nebula Finance has an extensive network of <span className="text-indigo-400 font-semibold">banks and financial institutions</span> across India and globally.
              We specialise in arranging competitive debt facilities — from working capital lines to long-term structured finance —
              ensuring you get the best pricing, terms, and speed of execution.
            </p>
            
          </div>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {[
          { id: 1, label: "Contact Details" },
          { id: 2, label: "Loan Requirements" },
          { id: 3, label: "Financial Profile" },
          { id: 4, label: "Documents & Submit" },
        ].map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              step === s.id ? "bg-indigo-600 text-white" :
              step > s.id  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
              "bg-[#1a1d26] text-gray-400 border border-[#2a2d3a]"
            }`}>
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold border border-current">{step > s.id ? "✓" : s.id}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < 3 && <span className="text-gray-600">→</span>}
          </div>
        ))}
      </div>

      <div className="nebula-card p-8">

        {/* Step 1 — Contact Details */}
        {step === 1 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Contact Details</h3>
            <p className="text-gray-400 text-sm mb-6">Tell us who we should get in touch with regarding this request.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Company Name <span className="text-red-400">*</span></label>
                <input value={form.company_name} onChange={e => set("company_name", e.target.value)} className="nebula-input" placeholder="Acme Corporation Ltd" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Contact Person Name <span className="text-red-400">*</span></label>
                <input value={form.contact_name} onChange={e => set("contact_name", e.target.value)} className="nebula-input" placeholder="John Doe" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email Address <span className="text-red-400">*</span></label>
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)} className="nebula-input" placeholder="john@company.com" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Phone Number <span className="text-red-400">*</span></label>
                <input
                type="tel"
                value={form.phone}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9+\s-]/g, "");
                  set("phone", val);
                }}
                className="nebula-input"
                placeholder="+91 98765 43210"
                maxLength={15}
              />
              <p className="text-xs text-gray-500 mt-1">Enter 10-digit number with country code e.g. +91 98765 43210</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Loan Requirements */}
        {step === 2 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Loan Requirements</h3>
            <p className="text-gray-400 text-sm mb-6">Provide details of the financing you are looking to raise.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Type of Debt <span className="text-red-400">*</span></label>
                <select value={form.debt_type} onChange={e => set("debt_type", e.target.value)} className="nebula-input">
                  <option value="">Select type...</option>
                  {DEBT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Currency <span className="text-red-400">*</span></label>
                <select value={form.currency} onChange={e => set("currency", e.target.value)} className="nebula-input">
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Amount Required <span className="text-red-400">*</span></label>
                <input type="number" value={form.amount_required} onChange={e => set("amount_required", e.target.value)} className="nebula-input" placeholder="e.g. 50000000" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Tenure (Months) <span className="text-red-400">*</span></label>
                <input type="number" value={form.tenure_months} onChange={e => set("tenure_months", e.target.value)} className="nebula-input" placeholder="e.g. 36" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Pricing Expectation (% p.a.) <span className="text-red-400">*</span></label>
                <input type="number" step="0.01" value={form.pricing_expectation} onChange={e => set("pricing_expectation", e.target.value)} className="nebula-input" placeholder="e.g. 9.5" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Collateral Available</label>
                <input value={form.collateral} onChange={e => set("collateral", e.target.value)} className="nebula-input" placeholder="e.g. Property, Stocks, None" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Existing Banker(s)</label>
                <input value={form.existing_banker} onChange={e => set("existing_banker", e.target.value)} className="nebula-input" placeholder="e.g. HDFC Bank, SBI" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Purpose of Loan <span className="text-red-400">*</span></label>
                <textarea value={form.purpose} onChange={e => set("purpose", e.target.value)} className="nebula-input h-24 resize-none" placeholder="Describe the purpose — e.g. expansion, working capital, refinancing existing debt..." />
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Financial Profile */}
        {step === 3 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Financial Profile</h3>
            <p className="text-gray-400 text-sm mb-6">This helps our banking partners assess your creditworthiness and structure the right facility.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Annual Turnover <span className="text-red-400">*</span></label>
                <input type="number" value={form.annual_turnover} onChange={e => set("annual_turnover", e.target.value)} className="nebula-input" placeholder="e.g. 100000000" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">EBITDA <span className="text-red-400">*</span></label>
                <input type="number" value={form.ebitda} onChange={e => set("ebitda", e.target.value)} className="nebula-input" placeholder="e.g. 20000000" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Existing Total Debt <span className="text-red-400">*</span></label>
                <input type="number" value={form.existing_debt} onChange={e => set("existing_debt", e.target.value)} className="nebula-input" placeholder="e.g. 50000000" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Credit Rating <span className="text-red-400">*</span></label>
                <select value={form.credit_rating} onChange={e => set("credit_rating", e.target.value)} className="nebula-input">
                  <option value="">Select rating...</option>
                  {RATINGS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Rating Provider <span className="text-red-400">*</span></label>
                <select value={form.rating_provider} onChange={e => set("rating_provider", e.target.value)} className="nebula-input">
                  <option value="">Select provider...</option>
                  {RATING_PROVIDERS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Additional Notes</label>
                <textarea value={form.additional_notes} onChange={e => set("additional_notes", e.target.value)} className="nebula-input h-24 resize-none" placeholder="Any other relevant information — covenants, special requirements, timeline urgency..." />
              </div>
            </div>
          </div>
        )}

        {/* Step 4 — Documents & Submit */}
        {step === 4 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Documents & Submit</h3>
            <p className="text-gray-400 text-sm mb-6">Upload your latest term sheet and review the terms before submitting.</p>

      {/* Upload term sheet */}
            <div className="mb-4">
              <label className="block text-xs text-gray-400 mb-2">Last Loan Term Sheet (PDF)</label>
              <div className="flex items-center gap-4 p-5 rounded-xl bg-[#0a0b0f] border border-dashed border-[#2a2d3a] hover:border-indigo-500/50 transition-all">
                <label className="nebula-btn-primary cursor-pointer flex items-center gap-2 text-sm px-4 py-2">
                  <Upload className="w-4 h-4" />
                  {termSheetFile ? "Change File" : "Upload Term Sheet"}
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => setTermSheetFile(e.target.files[0])} />
                </label>
                {termSheetFile ? (
                  <span className="text-emerald-400 text-sm">✓ {termSheetFile.name}</span>
                ) : (
                  <span className="text-gray-500 text-sm">PDF, DOC or DOCX · Max 10MB</span>
                )}
              </div>
            </div>

            {/* Upload rating rationale */}
            <div className="mb-6">
              <label className="block text-xs text-gray-400 mb-2">Latest Rating Rationale (PDF) <span className="text-gray-500">— Optional</span></label>
              <div className="flex items-center gap-4 p-5 rounded-xl bg-[#0a0b0f] border border-dashed border-[#2a2d3a] hover:border-purple-500/50 transition-all">
                <label className="flex items-center gap-2 px-4 py-2 rounded-xl border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 transition-all cursor-pointer text-sm font-medium">
                  <Upload className="w-4 h-4" />
                  {ratingFile ? "Change File" : "Upload Rating Report"}
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => setRatingFile(e.target.files[0])} />
                </label>
                {ratingFile ? (
                  <span className="text-emerald-400 text-sm">✓ {ratingFile.name}</span>
                ) : (
                  <span className="text-gray-500 text-sm">CRISIL, ICRA, CARE or any rating agency report</span>
                )}
              </div>
            </div>

            {/* Request summary */}
            <div className="nebula-card p-5 mb-6">
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Request Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><p className="text-gray-500 text-xs">Company</p><p className="text-white font-medium">{form.company_name || "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Contact</p><p className="text-white font-medium">{form.contact_name || "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Email</p><p className="text-white font-medium">{form.email || "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Debt Type</p><p className="text-white font-medium">{form.debt_type || "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Amount</p><p className="text-white font-medium">{form.currency} {form.amount_required ? parseFloat(form.amount_required).toLocaleString() : "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Pricing Expectation</p><p className="text-white font-medium">{form.pricing_expectation ? `${form.pricing_expectation}% p.a.` : "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Tenure</p><p className="text-white font-medium">{form.tenure_months ? `${form.tenure_months} months` : "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Credit Rating</p><p className="text-white font-medium">{form.credit_rating || "—"}</p></div>
                <div><p className="text-gray-500 text-xs">Term Sheet</p><p className="text-emerald-400 font-medium">{termSheetFile ? termSheetFile.name : "Not uploaded"}</p></div>
              </div>
            </div>

            {/* Contact details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Send request to</p>
                  <p className="text-white font-medium text-sm">founder@nebulafinance.net</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Call us</p>
                  <p className="text-white font-medium text-sm">+91 98765 43210</p>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="p-5 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
              <h4 className="text-sm font-bold text-amber-400 mb-2">⚠ Important Disclaimer</h4>
              <p className="text-amber-200/80 text-xs leading-relaxed mb-3">
                By submitting this request, you acknowledge and agree to the following:
              </p>
              <ul className="text-amber-200/70 text-xs space-y-1.5 mb-4 list-none">
                <li>→ <strong className="text-amber-300">Data Sharing:</strong> The information provided in this form, including your company financials, credit rating and loan requirements, will be shared with select third-party banking and financial institution partners of Nebula Finance for the purpose of arranging debt facilities.</li>
                <li>→ <strong className="text-amber-300">Arranger Fee:</strong> Nebula Finance charges an arranger fee upon successful closure of the debt facility. The fee will be communicated to you separately and is subject to mutual agreement before any binding commitment.</li>
                <li>→ <strong className="text-amber-300">No Guarantee:</strong> Submission of this form does not constitute a guarantee of financing. All facilities are subject to credit approval by the respective lending institution.</li>
                <li>→ <strong className="text-amber-300">Confidentiality:</strong> Nebula Finance will treat all information shared with utmost confidentiality and will not disclose it to any party outside the debt arrangement process.</li>
              </ul>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={disclaimerAccepted}
                  onChange={e => setDisclaimerAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-indigo-500"
                />
                <span className="text-amber-300 text-xs font-medium">
                  I have read and agree to the above disclaimer. I consent to Nebula Finance sharing my information with banking partners and acknowledge the arranger fee arrangement.
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#2a2d3a]">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="flex items-center gap-2 px-5 py-2.5 border border-[#2a2d3a] rounded-xl text-gray-400 hover:text-white hover:border-indigo-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed text-sm"
          >
            ← Previous
          </button>
          <span className="text-gray-500 text-sm">Step {step} of 4</span>
          {step < 4 ? (
            <button
              onClick={() => {
                if (step === 1 && !validateStep1()) return;
                if (step === 2 && !validateStep2()) return;
                if (step === 3 && !validateStep3()) return;
                setStep(s => s + 1);
              }}
              className="nebula-btn-primary flex items-center gap-2 px-5 py-2.5 text-sm"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving || !disclaimerAccepted}
              className="nebula-btn-primary flex items-center gap-2 px-6 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Submit Request →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}